export type MapFreshnessState = 'fresh' | 'stale' | 'unavailable' | 'disconnected';

export type MapFreshness = {
  observed_at?: string;
  received_at?: string;
  expected_interval_seconds: number;
  state: MapFreshnessState | string;
  reason?: string;
};

export type AlertSummary = {
  firing_count: number;
  highest_severity?: string;
  unacknowledged_count: number;
  alert_ids: string[];
};

export type MapCoordinate = { lat: number; lng: number };

export type HubDeviceHealth = 'healthy' | 'degraded' | 'critical' | 'unknown';

export type HubDeviceHealthSummary = {
  overall: HubDeviceHealth;
  monitored_count: number;
  healthy_count: number;
  degraded_count: number;
  critical_count: number;
  unknown_count: number;
  worst_freshness: string;
  confirmed_alert_count: number;
  unacknowledged_alert_count: number;
};

export type MapHub = {
  id: string;
  external_id: string;
  name?: string;
  location?: MapCoordinate;
  location_source: string;
  alert_summary: AlertSummary;
  device_health_summary?: HubDeviceHealthSummary;
};

export type MapEndpoint = {
  role: string;
  site_id: string;
  site_name?: string;
  device_id?: string;
  device_name?: string;
  device_host?: string;
  interface_id?: string;
  interface_name?: string;
  interface_ifindex?: number;
  location?: MapCoordinate;
};

export type MapLinkStatusReason = {
  code?: string;
  summary?: string;
  details?: string[];
  source_oper_status?: number;
  target_oper_status?: number;
  source_out_bps?: number;
  source_in_bps?: number;
  target_out_bps?: number;
  target_in_bps?: number;
  asymmetry_ratio?: number;
  min_load_threshold_bps?: number;
  near_zero_threshold_bps?: number;
  ratio_threshold?: number;
};

export type MapLink = {
  id: string;
  tenant_id: string;
  slug: string;
  display_name: string;
  link_kind?: string;
  link_type?: string;
  topology_status?: string;
  topology_reason?: string;
  topology_confidence?: number;
  topology_evidence_types?: string[];
  capacity_bps?: number;
  monitoring_enabled: boolean;
  updated_at: string;
  source: MapEndpoint;
  target: MapEndpoint;
  state: {
    observed_at: string;
    health: string;
    oper_state: string;
    ingress_bps?: number;
    egress_bps?: number;
    ingress_utilization_pct?: number;
    egress_utilization_pct?: number;
    missing_metrics?: string[];
    source_summary?: Record<string, unknown>;
    status_reason?: MapLinkStatusReason;
  };
  freshness: MapFreshness;
  alert_summary: AlertSummary;
};

export type MapDevice = {
  id: string;
  site_id: string;
  site_name?: string;
  hostname?: string;
  display_name: string;
  device_role?: string;
  monitoring_enabled: boolean;
  location?: MapCoordinate;
  reachability: string;
  collection_health: string;
  link_ids: string[];
  overlay_expected: number;
  overlay_ready: number;
  freshness: MapFreshness;
  alert_summary: AlertSummary;
};

export type StatusAlert = {
  alert_id: string;
  rule_key: string;
  severity: string;
  resource_kind: string;
  resource_id?: string;
  summary: string;
  opened_at: string;
  acknowledged: boolean;
  verification_state: 'confirmed' | 'pending_resolution' | 'unverified';
  verification_reason?: string;
  last_confirmed_at?: string;
  source_runtime_id?: string;
  suppressed: boolean;
  suppressed_until?: string;
};

export type StatusAlertTotals = {
  confirmed_total: number;
  critical_count: number;
  warning_count: number;
  info_count: number;
  acknowledged_count: number;
  unacknowledged_count: number;
  suppressed_count: number;
  unverified_count: number;
  returned_count: number;
  truncated: boolean;
};

export type StatusProjectionCacheMetadata = {
  state: 'fresh' | 'stale' | 'bypass' | 'unknown';
  age_seconds: number;
  warning?: string;
};

export type StatusMapSnapshot = {
  server_time: string;
  tenant_id: string;
  revision: string;
  hubs: MapHub[];
  devices: MapDevice[];
  links: MapLink[];
  active_alerts: StatusAlert[];
  unverified_alerts: StatusAlert[];
  alert_totals: StatusAlertTotals;
  cache_metadata: StatusProjectionCacheMetadata;
  freshness: Record<'inventory' | 'telemetry' | 'alerting', MapFreshness>;
  observability: {
    grafana_available: boolean;
    dashboard_uid?: string;
  };
};

export type MapHubPatch = {
  id: string;
  alert_summary?: AlertSummary;
  device_health_summary: HubDeviceHealthSummary | null;
};

export type MapDevicePatch = {
  id: string;
  reachability: string;
  collection_health: string;
  freshness: MapFreshness;
  alert_summary?: AlertSummary;
};

export type MapLinkPatch = {
  id: string;
  state: MapLink['state'];
  freshness: MapFreshness;
  updated_at: string;
  alert_summary?: AlertSummary;
};

export type StatusMapEntityUpserts = {
  hubs: MapHub[];
  devices: MapDevice[];
  links: MapLink[];
  alerts: StatusAlert[];
};

export type StatusMapEntityPatches = {
  hubs: MapHubPatch[];
  devices: MapDevicePatch[];
  links: MapLinkPatch[];
};

export type StatusMapRemoved = {
  hubs: string[];
  devices: string[];
  links: string[];
  alerts: string[];
};

export type StatusMapChanges = {
  tenant_id: string;
  base_revision: string;
  revision: string;
  current_revision: string;
  earliest_revision: string;
  server_time: string;
  has_more: boolean;
  reload_required: boolean;
  upserts: StatusMapEntityUpserts;
  patches: StatusMapEntityPatches;
  removed: StatusMapRemoved;
  freshness: Partial<Record<'inventory' | 'telemetry' | 'alerting', MapFreshness>>;
  observability?: StatusMapSnapshot['observability'] | null;
  alert_totals?: StatusAlertTotals;
};

const readJSONResponse = async <T,>(url: string, signal?: AbortSignal) => {
  const response = await fetch(url, { credentials: 'include', cache: 'no-store', signal });
  if (!response.ok) throw new Error(`Request failed with status ${response.status}`);
  return { payload: (await response.json()) as T, response };
};

const readJSON = async <T,>(url: string, signal?: AbortSignal): Promise<T> =>
  (await readJSONResponse<T>(url, signal)).payload;

export type StatusTelemetryClient = {
  fetchSnapshot(signal?: AbortSignal): Promise<StatusMapSnapshot>;
  fetchChanges(after: string, signal?: AbortSignal): Promise<StatusMapChanges>;
  fetchDeviceMetrics(deviceID: string, signal?: AbortSignal): Promise<DeviceMetricsResponse>;
};

export type DeviceMetricAvailability =
  | 'available'
  | 'unsupported'
  | 'unavailable'
  | 'stale'
  | 'failed';

export type DeviceMetric = {
  key: string;
  label: string;
  availability: DeviceMetricAvailability;
  observed_at?: string;
  value?: number | string | boolean;
  state?: string;
  unit?: string;
  component_id?: string;
  component_name?: string;
  component_kind?: string;
  interface_id?: string;
  diagnostic?: string;
};

export type DeviceMetricCategory =
  | 'resource'
  | 'environment'
  | 'hardware'
  | 'routing'
  | 'collection';

export type DeviceMetricGroup = {
  category: DeviceMetricCategory;
  availability: DeviceMetricAvailability;
  observed_at?: string;
  metrics: DeviceMetric[];
  returned_count: number;
  total_count: number;
  truncated: boolean;
  diagnostic?: string;
};

export type DeviceMetricsResponse = {
  tenant_id: string;
  server_time: string;
  device: {
    id: string;
    site_id: string;
    site_name?: string;
    hostname?: string;
    display_name: string;
    device_role?: string;
  };
  reachability: string;
  collection_health: string;
  freshness: MapFreshness;
  alert_summary: AlertSummary;
  alerts: StatusAlert[];
  alert_total: number;
  alert_returned: number;
  alerts_truncated: boolean;
  groups: DeviceMetricGroup[];
};

type StatusMapSnapshotAlertPayload = Omit<StatusAlert, 'verification_state' | 'suppressed'> &
  Partial<Pick<StatusAlert, 'verification_state' | 'suppressed'>>;
type StatusMapSnapshotCollections = Pick<StatusMapSnapshot, 'hubs' | 'devices' | 'links'> & {
  active_alerts: StatusMapSnapshotAlertPayload[];
  unverified_alerts: StatusMapSnapshotAlertPayload[];
  alert_totals: StatusAlertTotals;
};
type StatusMapSnapshotPayload = Omit<
  StatusMapSnapshot,
  keyof StatusMapSnapshotCollections | 'cache_metadata'
> &
  Partial<StatusMapSnapshotCollections>;

export class StatusMapDeltaPayloadError extends Error {}

const normalizeAlertSummary = (summary?: AlertSummary): AlertSummary => ({
  firing_count: Number.isFinite(summary?.firing_count) ? Number(summary?.firing_count) : 0,
  highest_severity: typeof summary?.highest_severity === 'string' ? summary.highest_severity : undefined,
  unacknowledged_count: Number.isFinite(summary?.unacknowledged_count)
    ? Number(summary?.unacknowledged_count)
    : 0,
  alert_ids: Array.isArray(summary?.alert_ids)
    ? summary.alert_ids.filter((id): id is string => typeof id === 'string')
    : []
});

const normalizeHubDeviceHealthSummary = (
  summary?: Partial<HubDeviceHealthSummary>
): HubDeviceHealthSummary | undefined => {
  if (!summary) return undefined;
  const overall = ['healthy', 'degraded', 'critical', 'unknown'].includes(String(summary.overall))
    ? (summary.overall as HubDeviceHealth)
    : 'unknown';
  const count = (value: unknown) => (Number.isFinite(value) ? Math.max(0, Number(value)) : 0);
  return {
    overall,
    monitored_count: count(summary.monitored_count),
    healthy_count: count(summary.healthy_count),
    degraded_count: count(summary.degraded_count),
    critical_count: count(summary.critical_count),
    unknown_count: count(summary.unknown_count),
    worst_freshness:
      typeof summary.worst_freshness === 'string' ? summary.worst_freshness : 'unknown',
    confirmed_alert_count: count(summary.confirmed_alert_count),
    unacknowledged_alert_count: count(summary.unacknowledged_alert_count)
  };
};

const emptyAlertTotals = (): StatusAlertTotals => ({
  confirmed_total: 0,
  critical_count: 0,
  warning_count: 0,
  info_count: 0,
  acknowledged_count: 0,
  unacknowledged_count: 0,
  suppressed_count: 0,
  unverified_count: 0,
  returned_count: 0,
  truncated: false
});

const normalizeAlertTotals = (totals?: Partial<StatusAlertTotals>): StatusAlertTotals => ({
  ...emptyAlertTotals(),
  ...totals
});

const normalizeStatusAlert = (alert: StatusMapSnapshotAlertPayload): StatusAlert => ({
  ...alert,
  verification_state: alert.verification_state ?? 'confirmed',
  suppressed: alert.suppressed ?? false
});

export const normalizeStatusMapSnapshot = (snapshot: StatusMapSnapshotPayload): StatusMapSnapshot => ({
  ...snapshot,
  hubs: Array.isArray(snapshot.hubs)
    ? snapshot.hubs.map(hub => ({
        ...hub,
        alert_summary: normalizeAlertSummary(hub.alert_summary),
        device_health_summary: normalizeHubDeviceHealthSummary(hub.device_health_summary)
      }))
    : [],
  devices: Array.isArray(snapshot.devices)
    ? snapshot.devices.map(device => ({
        ...device,
        link_ids: Array.isArray(device.link_ids) ? device.link_ids : [],
        alert_summary: normalizeAlertSummary(device.alert_summary)
      }))
    : [],
  links: Array.isArray(snapshot.links)
    ? snapshot.links.map(link => ({ ...link, alert_summary: normalizeAlertSummary(link.alert_summary) }))
    : [],
  active_alerts: Array.isArray(snapshot.active_alerts)
    ? snapshot.active_alerts
        .map(normalizeStatusAlert)
        .filter(alert => alert.verification_state !== 'unverified')
    : [],
  unverified_alerts: [
    ...(Array.isArray(snapshot.active_alerts)
      ? snapshot.active_alerts
          .map(normalizeStatusAlert)
          .filter(alert => alert.verification_state === 'unverified')
      : []),
    ...(Array.isArray(snapshot.unverified_alerts)
      ? snapshot.unverified_alerts.map(normalizeStatusAlert)
      : [])
  ],
  alert_totals: normalizeAlertTotals(snapshot.alert_totals),
  cache_metadata: { state: 'unknown', age_seconds: 0 }
});

const projectionCacheMetadata = (response: Response): StatusProjectionCacheMetadata => {
  const rawState = response.headers.get('X-Status-Cache');
  const state =
    rawState === 'fresh' || rawState === 'stale' || rawState === 'bypass'
      ? rawState
      : 'unknown';
  const rawAge = Number(response.headers.get('Age') ?? 0);
  return {
    state,
    age_seconds: Number.isFinite(rawAge) && rawAge >= 0 ? rawAge : 0,
    warning: response.headers.get('Warning') ?? undefined
  };
};

const asObject = (value: unknown, path: string): Record<string, unknown> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new StatusMapDeltaPayloadError(`Invalid map delta ${path}`);
  }
  return value as Record<string, unknown>;
};

const asString = (value: unknown, path: string) => {
  if (typeof value !== 'string' || !value.trim()) {
    throw new StatusMapDeltaPayloadError(`Invalid map delta ${path}`);
  }
  return value;
};

const asPossiblyEmptyString = (value: unknown, path: string) => {
  if (typeof value !== 'string') {
    throw new StatusMapDeltaPayloadError(`Invalid map delta ${path}`);
  }
  return value;
};

const asRevision = (value: unknown, path: string) => {
  const revision = asString(value, path);
  const numeric = Number(revision);
  if (!/^\d+$/.test(revision) || !Number.isSafeInteger(numeric) || numeric < 0) {
    throw new StatusMapDeltaPayloadError(`Invalid map delta ${path}`);
  }
  return revision;
};

const asBoolean = (value: unknown, path: string) => {
  if (typeof value !== 'boolean') throw new StatusMapDeltaPayloadError(`Invalid map delta ${path}`);
  return value;
};

const asNumber = (value: unknown, path: string) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new StatusMapDeltaPayloadError(`Invalid map delta ${path}`);
  }
  return value;
};

const asRequiredArray = (value: unknown, path: string) => {
  if (!Array.isArray(value)) throw new StatusMapDeltaPayloadError(`Invalid map delta ${path}`);
  return value;
};

const normalizeFreshness = (value: unknown, path: string): MapFreshness => {
  const item = asObject(value, path);
  const expectedInterval = Number(item.expected_interval_seconds);
  if (!Number.isFinite(expectedInterval) || expectedInterval <= 0) {
    throw new StatusMapDeltaPayloadError(`Invalid map delta ${path}.expected_interval_seconds`);
  }
  return {
    observed_at: typeof item.observed_at === 'string' ? item.observed_at : undefined,
    received_at: typeof item.received_at === 'string' ? item.received_at : undefined,
    expected_interval_seconds: expectedInterval,
    state: asString(item.state, `${path}.state`),
    reason: typeof item.reason === 'string' ? item.reason : undefined
  };
};

const normalizePatchAlertSummary = (value: unknown, path: string) => {
  const summary = asObject(value, path);
  asNumber(summary.firing_count, `${path}.firing_count`);
  asNumber(summary.unacknowledged_count, `${path}.unacknowledged_count`);
  asRequiredArray(summary.alert_ids, `${path}.alert_ids`).forEach((id, index) =>
    asString(id, `${path}.alert_ids[${index}]`)
  );
  return normalizeAlertSummary(summary as AlertSummary);
};

const normalizeDeltaHubDeviceHealthSummary = (
  value: unknown,
  path: string
): HubDeviceHealthSummary => {
  const summary = asObject(value, path);
  const overall = asString(summary.overall, `${path}.overall`);
  if (!['healthy', 'degraded', 'critical', 'unknown'].includes(overall)) {
    throw new StatusMapDeltaPayloadError(`Invalid map delta ${path}.overall`);
  }
  return {
    overall: overall as HubDeviceHealth,
    monitored_count: asNumber(summary.monitored_count, `${path}.monitored_count`),
    healthy_count: asNumber(summary.healthy_count, `${path}.healthy_count`),
    degraded_count: asNumber(summary.degraded_count, `${path}.degraded_count`),
    critical_count: asNumber(summary.critical_count, `${path}.critical_count`),
    unknown_count: asNumber(summary.unknown_count, `${path}.unknown_count`),
    worst_freshness: asString(summary.worst_freshness, `${path}.worst_freshness`),
    confirmed_alert_count: asNumber(
      summary.confirmed_alert_count,
      `${path}.confirmed_alert_count`
    ),
    unacknowledged_alert_count: asNumber(
      summary.unacknowledged_alert_count,
      `${path}.unacknowledged_alert_count`
    )
  };
};

const normalizeHubUpsert = (value: unknown, index: number): MapHub => {
  const path = `upserts.hubs[${index}]`;
  const item = asObject(value, path);
  asString(item.id, `${path}.id`);
  asPossiblyEmptyString(item.external_id, `${path}.external_id`);
  asString(item.location_source, `${path}.location_source`);
  normalizePatchAlertSummary(item.alert_summary, `${path}.alert_summary`);
  if (item.device_health_summary !== undefined) {
    normalizeDeltaHubDeviceHealthSummary(
      item.device_health_summary,
      `${path}.device_health_summary`
    );
  }
  if (item.location !== undefined) {
    const location = asObject(item.location, `${path}.location`);
    asNumber(location.lat, `${path}.location.lat`);
    asNumber(location.lng, `${path}.location.lng`);
  }
  return {
    ...(item as MapHub),
    alert_summary: normalizeAlertSummary(item.alert_summary as AlertSummary | undefined),
    device_health_summary: normalizeHubDeviceHealthSummary(
      item.device_health_summary as HubDeviceHealthSummary | undefined
    )
  };
};

const normalizeDeviceUpsert = (value: unknown, index: number): MapDevice => {
  const path = `upserts.devices[${index}]`;
  const item = asObject(value, path);
  asString(item.id, `${path}.id`);
  asString(item.site_id, `${path}.site_id`);
  asString(item.display_name, `${path}.display_name`);
  asBoolean(item.monitoring_enabled, `${path}.monitoring_enabled`);
  asString(item.reachability, `${path}.reachability`);
  asString(item.collection_health, `${path}.collection_health`);
  asNumber(item.overlay_expected, `${path}.overlay_expected`);
  asNumber(item.overlay_ready, `${path}.overlay_ready`);
  normalizeFreshness(item.freshness, `${path}.freshness`);
  normalizePatchAlertSummary(item.alert_summary, `${path}.alert_summary`);
  if (!Array.isArray(item.link_ids)) {
    throw new StatusMapDeltaPayloadError(`Invalid map delta ${path}.link_ids`);
  }
  return {
    ...(item as MapDevice),
    link_ids: Array.isArray(item.link_ids)
      ? item.link_ids.filter((id): id is string => typeof id === 'string')
      : [],
    alert_summary: normalizeAlertSummary(item.alert_summary as AlertSummary | undefined)
  };
};

const normalizeLinkUpsert = (value: unknown, index: number): MapLink => {
  const path = `upserts.links[${index}]`;
  const item = asObject(value, path);
  asString(item.id, `${path}.id`);
  asString(item.tenant_id, `${path}.tenant_id`);
  asString(item.slug, `${path}.slug`);
  asString(item.display_name, `${path}.display_name`);
  asBoolean(item.monitoring_enabled, `${path}.monitoring_enabled`);
  asString(item.updated_at, `${path}.updated_at`);
  (['source', 'target'] as const).forEach(endpointKey => {
    const endpoint = asObject(item[endpointKey], `${path}.${endpointKey}`);
    asString(endpoint.role, `${path}.${endpointKey}.role`);
    asString(endpoint.site_id, `${path}.${endpointKey}.site_id`);
  });
  const state = asObject(item.state, `${path}.state`);
  asString(state.observed_at, `${path}.state.observed_at`);
  asString(state.health, `${path}.state.health`);
  asString(state.oper_state, `${path}.state.oper_state`);
  normalizeFreshness(item.freshness, `${path}.freshness`);
  normalizePatchAlertSummary(item.alert_summary, `${path}.alert_summary`);
  return {
    ...(item as MapLink),
    alert_summary: normalizeAlertSummary(item.alert_summary as AlertSummary | undefined)
  };
};

const normalizeAlertUpsert = (value: unknown, index: number): StatusAlert => {
  const path = `upserts.alerts[${index}]`;
  const item = asObject(value, path);
  asString(item.alert_id, `${path}.alert_id`);
  asString(item.rule_key, `${path}.rule_key`);
  asString(item.severity, `${path}.severity`);
  asString(item.resource_kind, `${path}.resource_kind`);
  asString(item.summary, `${path}.summary`);
  asString(item.opened_at, `${path}.opened_at`);
  asBoolean(item.acknowledged, `${path}.acknowledged`);
  const verificationState = asString(item.verification_state, `${path}.verification_state`);
  if (!['confirmed', 'pending_resolution', 'unverified'].includes(verificationState)) {
    throw new StatusMapDeltaPayloadError(`Invalid map delta ${path}.verification_state`);
  }
  asBoolean(item.suppressed, `${path}.suppressed`);
  return item as StatusAlert;
};

const normalizeDeltaAlertTotals = (value: unknown): StatusAlertTotals => {
  const totals = asObject(value, 'alert_totals');
  return {
    confirmed_total: asNumber(totals.confirmed_total, 'alert_totals.confirmed_total'),
    critical_count: asNumber(totals.critical_count, 'alert_totals.critical_count'),
    warning_count: asNumber(totals.warning_count, 'alert_totals.warning_count'),
    info_count: asNumber(totals.info_count, 'alert_totals.info_count'),
    acknowledged_count: asNumber(totals.acknowledged_count, 'alert_totals.acknowledged_count'),
    unacknowledged_count: asNumber(
      totals.unacknowledged_count,
      'alert_totals.unacknowledged_count'
    ),
    suppressed_count: asNumber(totals.suppressed_count, 'alert_totals.suppressed_count'),
    unverified_count: asNumber(totals.unverified_count, 'alert_totals.unverified_count'),
    returned_count: asNumber(totals.returned_count, 'alert_totals.returned_count'),
    truncated: asBoolean(totals.truncated, 'alert_totals.truncated')
  };
};

const normalizeRemoved = (value: unknown, path: string) =>
  asRequiredArray(value, path).map((id, index) => asString(id, `${path}[${index}]`));

export const normalizeStatusMapChanges = (value: unknown): StatusMapChanges => {
  const response = asObject(value, 'response');
  const upserts = asObject(response.upserts, 'upserts');
  const patches = asObject(response.patches, 'patches');
  const removed = asObject(response.removed, 'removed');
  const freshnessValue = asObject(response.freshness, 'freshness');

  const hubPatches = asRequiredArray(patches.hubs, 'patches.hubs').map((value, index): MapHubPatch => {
    const patch = asObject(value, `patches.hubs[${index}]`);
    if (!Object.prototype.hasOwnProperty.call(patch, 'device_health_summary')) {
      throw new StatusMapDeltaPayloadError(
        `Invalid map delta patches.hubs[${index}].device_health_summary`
      );
    }
    return {
      id: asString(patch.id, `patches.hubs[${index}].id`),
      alert_summary: patch.alert_summary === undefined
        ? undefined
        : normalizePatchAlertSummary(
            patch.alert_summary,
            `patches.hubs[${index}].alert_summary`
          ),
      device_health_summary:
        patch.device_health_summary === null
          ? null
          : normalizeDeltaHubDeviceHealthSummary(
              patch.device_health_summary,
              `patches.hubs[${index}].device_health_summary`
            )
    };
  });
  const devicePatches = asRequiredArray(patches.devices, 'patches.devices').map((value, index): MapDevicePatch => {
    const patch = asObject(value, `patches.devices[${index}]`);
    return {
      id: asString(patch.id, `patches.devices[${index}].id`),
      reachability: asString(patch.reachability, `patches.devices[${index}].reachability`),
      collection_health: asString(
        patch.collection_health,
        `patches.devices[${index}].collection_health`
      ),
      freshness: normalizeFreshness(patch.freshness, `patches.devices[${index}].freshness`),
      alert_summary: patch.alert_summary === undefined
        ? undefined
        : normalizePatchAlertSummary(
            patch.alert_summary,
            `patches.devices[${index}].alert_summary`
          )
    };
  });
  const linkPatches = asRequiredArray(patches.links, 'patches.links').map((value, index): MapLinkPatch => {
    const patch = asObject(value, `patches.links[${index}]`);
    const state = asObject(patch.state, `patches.links[${index}].state`) as MapLink['state'];
    asString(state.observed_at, `patches.links[${index}].state.observed_at`);
    asString(state.health, `patches.links[${index}].state.health`);
    asString(state.oper_state, `patches.links[${index}].state.oper_state`);
    return {
      id: asString(patch.id, `patches.links[${index}].id`),
      state,
      freshness: normalizeFreshness(patch.freshness, `patches.links[${index}].freshness`),
      updated_at: asString(patch.updated_at, `patches.links[${index}].updated_at`),
      alert_summary: patch.alert_summary === undefined
        ? undefined
        : normalizePatchAlertSummary(
            patch.alert_summary,
            `patches.links[${index}].alert_summary`
          )
    };
  });

  const freshness: StatusMapChanges['freshness'] = {};
  (['inventory', 'telemetry', 'alerting'] as const).forEach(key => {
    if (freshnessValue[key] !== undefined) {
      freshness[key] = normalizeFreshness(freshnessValue[key], `freshness.${key}`);
    }
  });

  let observability: StatusMapChanges['observability'];
  if (response.observability === null) {
    observability = null;
  } else if (response.observability !== undefined) {
    const candidate = asObject(response.observability, 'observability');
    observability = {
      grafana_available: asBoolean(candidate.grafana_available, 'observability.grafana_available'),
      dashboard_uid: typeof candidate.dashboard_uid === 'string' ? candidate.dashboard_uid : undefined
    };
  }

  return {
    tenant_id: asString(response.tenant_id, 'tenant_id'),
    base_revision: asRevision(response.base_revision, 'base_revision'),
    revision: asRevision(response.revision, 'revision'),
    current_revision: asRevision(response.current_revision, 'current_revision'),
    earliest_revision: asRevision(response.earliest_revision, 'earliest_revision'),
    server_time: asString(response.server_time, 'server_time'),
    has_more: asBoolean(response.has_more, 'has_more'),
    reload_required: asBoolean(response.reload_required, 'reload_required'),
    upserts: {
      hubs: asRequiredArray(upserts.hubs, 'upserts.hubs').map(normalizeHubUpsert),
      devices: asRequiredArray(upserts.devices, 'upserts.devices').map(normalizeDeviceUpsert),
      links: asRequiredArray(upserts.links, 'upserts.links').map(normalizeLinkUpsert),
      alerts: asRequiredArray(upserts.alerts, 'upserts.alerts').map(normalizeAlertUpsert)
    },
    patches: {
      hubs: hubPatches,
      devices: devicePatches,
      links: linkPatches
    },
    removed: {
      hubs: normalizeRemoved(removed.hubs, 'removed.hubs'),
      devices: normalizeRemoved(removed.devices, 'removed.devices'),
      links: normalizeRemoved(removed.links, 'removed.links'),
      alerts: normalizeRemoved(removed.alerts, 'removed.alerts')
    },
    freshness,
    observability,
    alert_totals:
      response.alert_totals === undefined
        ? undefined
        : normalizeDeltaAlertTotals(response.alert_totals)
  };
};

const DEVICE_METRIC_CATEGORIES: DeviceMetricCategory[] = [
  'resource',
  'environment',
  'hardware',
  'routing',
  'collection'
];

const normalizeDeviceMetricAvailability = (value: unknown): DeviceMetricAvailability =>
  ['available', 'unsupported', 'unavailable', 'stale', 'failed'].includes(String(value))
    ? (value as DeviceMetricAvailability)
    : 'unavailable';

export const normalizeDeviceMetricsResponse = (value: unknown): DeviceMetricsResponse => {
  const response = asObject(value, 'device metrics response');
  const device = asObject(response.device, 'device metrics response.device');
  const freshness = normalizeFreshness(
    response.freshness,
    'device metrics response.freshness'
  );
  const groups = Array.isArray(response.groups) ? response.groups : [];
  return {
    tenant_id: asString(response.tenant_id, 'device metrics response.tenant_id'),
    server_time: asString(response.server_time, 'device metrics response.server_time'),
    device: {
      id: asString(device.id, 'device metrics response.device.id'),
      site_id: asString(device.site_id, 'device metrics response.device.site_id'),
      site_name: typeof device.site_name === 'string' ? device.site_name : undefined,
      hostname: typeof device.hostname === 'string' ? device.hostname : undefined,
      display_name: asString(
        device.display_name,
        'device metrics response.device.display_name'
      ),
      device_role: typeof device.device_role === 'string' ? device.device_role : undefined
    },
    reachability: asString(
      response.reachability,
      'device metrics response.reachability'
    ),
    collection_health: asString(
      response.collection_health,
      'device metrics response.collection_health'
    ),
    freshness,
    alert_summary: normalizeAlertSummary(response.alert_summary as AlertSummary | undefined),
    alerts: (Array.isArray(response.alerts) ? response.alerts : [])
      .filter(alert => alert && typeof alert === 'object')
      .map(alert => normalizeStatusAlert(alert as StatusMapSnapshotAlertPayload)),
    alert_total: Number.isFinite(response.alert_total) ? Number(response.alert_total) : 0,
    alert_returned: Number.isFinite(response.alert_returned)
      ? Number(response.alert_returned)
      : 0,
    alerts_truncated: response.alerts_truncated === true,
    groups: groups.flatMap((rawGroup): DeviceMetricGroup[] => {
      if (!rawGroup || typeof rawGroup !== 'object') return [];
      const group = rawGroup as Record<string, unknown>;
      if (!DEVICE_METRIC_CATEGORIES.includes(group.category as DeviceMetricCategory)) return [];
      const metrics = Array.isArray(group.metrics) ? group.metrics : [];
      return [{
        category: group.category as DeviceMetricCategory,
        availability: normalizeDeviceMetricAvailability(group.availability),
        observed_at: typeof group.observed_at === 'string' ? group.observed_at : undefined,
        metrics: metrics.flatMap((rawMetric): DeviceMetric[] => {
          if (!rawMetric || typeof rawMetric !== 'object') return [];
          const metric = rawMetric as Record<string, unknown>;
          if (typeof metric.key !== 'string' || typeof metric.label !== 'string') return [];
          const metricValue = metric.value;
          return [{
            key: metric.key,
            label: metric.label,
            availability: normalizeDeviceMetricAvailability(metric.availability),
            observed_at:
              typeof metric.observed_at === 'string' ? metric.observed_at : undefined,
            value:
              typeof metricValue === 'number' ||
              typeof metricValue === 'string' ||
              typeof metricValue === 'boolean'
                ? metricValue
                : undefined,
            state: typeof metric.state === 'string' ? metric.state : undefined,
            unit: typeof metric.unit === 'string' ? metric.unit : undefined,
            component_id:
              typeof metric.component_id === 'string' ? metric.component_id : undefined,
            component_name:
              typeof metric.component_name === 'string' ? metric.component_name : undefined,
            component_kind:
              typeof metric.component_kind === 'string' ? metric.component_kind : undefined,
            interface_id:
              typeof metric.interface_id === 'string' ? metric.interface_id : undefined,
            diagnostic:
              typeof metric.diagnostic === 'string' ? metric.diagnostic : undefined
          }];
        }),
        returned_count: Number.isFinite(group.returned_count)
          ? Number(group.returned_count)
          : metrics.length,
        total_count: Number.isFinite(group.total_count)
          ? Number(group.total_count)
          : metrics.length,
        truncated: group.truncated === true,
        diagnostic: typeof group.diagnostic === 'string' ? group.diagnostic : undefined
      }];
    })
  };
};

export const statusTelemetryClient: StatusTelemetryClient = {
  fetchSnapshot: async signal => {
    const { payload, response } = await readJSONResponse<StatusMapSnapshotPayload>(
      '/api/v1/status/map/snapshot',
      signal
    );
    return {
      ...normalizeStatusMapSnapshot(payload),
      cache_metadata: projectionCacheMetadata(response)
    };
  },
  fetchChanges: async (after, signal) =>
    normalizeStatusMapChanges(
      await readJSON<unknown>(
        `/api/v1/status/map/changes?after=${encodeURIComponent(after)}`,
        signal
      )
    ),
  fetchDeviceMetrics: async (deviceID, signal) =>
    normalizeDeviceMetricsResponse(
      await readJSON<unknown>(
        `/api/v1/status/devices/${encodeURIComponent(deviceID)}/metrics`,
        signal
      )
    )
};

export const createPublicStatusTelemetryClient = (tenantSlug: string): StatusTelemetryClient => {
  const snapshotPath = `/api/v1/public/status/${encodeURIComponent(tenantSlug)}/map/snapshot`;
  return {
    fetchSnapshot: async signal => {
      const response = await fetch(snapshotPath, { credentials: 'omit', cache: 'no-store', signal });
      if (!response.ok) throw new Error(`Request failed with status ${response.status}`);
      const payload = (await response.json()) as StatusMapSnapshotPayload;
      return {
        ...normalizeStatusMapSnapshot(payload),
        cache_metadata: projectionCacheMetadata(response)
      };
    },
    fetchChanges: async () => {
      throw new Error('Public status maps use snapshot polling');
    },
    fetchDeviceMetrics: async () => {
      throw new Error('Device metrics are not available on public status maps');
    }
  };
};

export type GrafanaDrilldown = {
  deviceId: string;
  ifindex?: number;
  from?: string;
  to?: string;
};

export const grafanaDrilldownURL = ({ deviceId, ifindex, from, to }: GrafanaDrilldown) => {
  const params = new URLSearchParams({ device_id: deviceId });
  if (ifindex !== undefined) params.set('ifindex', String(ifindex));
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  return `/api/v1/status/observability/grafana/drilldown?${params.toString()}`;
};
